import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Row, Col, Alert, Spinner } from 'react-bootstrap';
import EventForm from './EventForm';
import axiosInstance from '../../services/axiosConfig';
import axios from 'axios';
import { categoryOptions, isSystemDefinedField } from '../../utils/eventCategories';

const EditEvent = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // UISP theme styles
  const uispStyles = {
    formLabel: { color: "#333" },
    primaryButton: { backgroundColor: "#006e39", borderColor: "#006e39", color: "white" },
    secondaryButton: { backgroundColor: "#555", color: "white", borderColor: "#555" },
    header: { color: "#006e39" }
  };

  // Spiegazione modalità iscrizione
  const registrationModeHelp = {
    team: "Iscrizione squadre: l'associazione iscrive una o più squadre/formazioni/gruppi. Ogni squadra può essere composta da più membri, e la registrazione avviene per gruppo.",
    individual: "Seleziona soci individuali: l'associazione iscrive i singoli soci partecipanti all'evento. Ogni partecipante viene selezionato individualmente dall'elenco soci."
  };

  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    location: '',
    eventDate: '',
    eventEndDate: '',
    startTime: '',
    endTime: '',
    registrationDeadline: '',
    maxParticipants: '',
    category: '',
    scope: 'territorial',
    price: '',
    isPublic: true,
    allowLateRegistration: false,
    image: null,
    documents: [],
    registrationFields: [],
    regulation: null,
    registrationMode: 'team'
  });

  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [locationQuery, setLocationQuery] = useState('');
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const searchTimeoutRef = useRef(null);  // Funzione per cercare indirizzi utilizzando OpenStreetMap Nominatim
  const searchLocations = async (query) => {
    if (!query || query.length < 3) {
      setLocationSuggestions([]);
      return;
    }

    setIsLoadingLocation(true);

    try {
      // Usiamo l'API di OpenStreetMap Nominatim per la ricerca degli indirizzi
      const response = await axios.get(`https://nominatim.openstreetmap.org/search`, {
        params: {
          q: query,
          format: 'json',
          addressdetails: 1,
          limit: 5
        },
        headers: {
          'Accept-Language': 'it' // Risultati in italiano
        }
      });

      const suggestions = response.data.map(item => ({
        id: item.place_id,
        display_name: item.display_name,
        lat: item.lat,
        lon: item.lon
      }));

      setLocationSuggestions(suggestions);
    } catch (error) {
      console.error('Errore nella ricerca degli indirizzi:', error);
    } finally {
      setIsLoadingLocation(false);
    }
  };

  // Gestisce il cambio del campo di ricerca indirizzo con debounce
  const handleLocationSearch = (e) => {
    const query = e.target.value;
    setLocationQuery(query);

    // Implementazione di debounce per limitare le richieste API
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchLocations(query);
    }, 500);
  };

  // Gestisce la selezione di un indirizzo dalla lista dei suggerimenti
  const handleSelectLocation = (location) => {
    setSelectedLocation(location);
    setLocationQuery(location.display_name);
    if (event) {
      setEvent({
        ...event,
        location: location.display_name
      });
    }
    setLocationSuggestions([]);
  };

  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        setLoading(true);
        setError('');

        const token = localStorage.getItem('token');
        if (!token) {
          console.log('No token found, redirecting to login');
          navigate('/login');
          return;
        }

        const response = await axiosInstance.get(`/api/events/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        let eventData;

        if (response.data && response.data.event) {
          eventData = response.data.event;
        } else if (response.data) {
          console.log('Event data in different format:', response.data);
          // Handle cases where the API returns event directly instead of wrapped
          eventData = response.data;
          console.log('Event data set directly from response');
        } else {
          console.log('No event data found in response');
          setError('Dati evento non trovati nella risposta');
          setLoading(false);
          return;
        }

        // Process registration fields if they are stored as a string
        if (typeof eventData.registrationFields === 'string') {
          try {
            eventData.registrationFields = JSON.parse(eventData.registrationFields);
          } catch (e) {
            console.error('Error parsing registration fields:', e);
            eventData.registrationFields = [];
          }
        } else if (!Array.isArray(eventData.registrationFields)) {
          eventData.registrationFields = [];
        }

        // Ensure registrationMode is set
        if (!eventData.registrationMode) {
          // Default to team or individual based on category
          if (['danza', 'calcio', 'pallacanestro', 'pallavolo'].includes(eventData.category)) {
            eventData.registrationMode = 'team';
          } else if (['manifestazione', 'atletica leggera', 'ciclismo', 'tennis'].includes(eventData.category)) {
            eventData.registrationMode = 'individual';
          } else {
            eventData.registrationMode = 'team'; // default
          }
        }

        // Set location query for location search functionality
        if (eventData.location) {
          setLocationQuery(eventData.location);
        }

        setEvent(eventData);

        // Update eventForm state with the fetched data
        setEventForm({
          title: eventData.title || '',
          description: eventData.description || '',
          location: eventData.location || '',
          eventDate: eventData.eventDate || eventData.event_date ?
            new Date(eventData.eventDate || eventData.event_date).toISOString().split('T')[0] : '',
          eventEndDate: eventData.eventEndDate || eventData.event_end_date || eventData.eventDate || eventData.event_date ?
            new Date(eventData.eventEndDate || eventData.event_end_date || eventData.eventDate || eventData.event_date).toISOString().split('T')[0] : '',
          startTime: eventData.startTime || eventData.start_time || '',
          endTime: eventData.endTime || eventData.end_time || '',
          registrationDeadline: eventData.registrationDeadline ?
            new Date(eventData.registrationDeadline).toISOString().slice(0, 16) : '',
          maxParticipants: eventData.maxParticipants || eventData.max_participants || '',
          category: eventData.category || '',
          scope: eventData.scope || 'territorial',
          price: eventData.price || '',
          isPublic: eventData.isPublic !== undefined ? eventData.isPublic : true,
          allowLateRegistration: eventData.allowLateRegistration || false,
          image: null,
          documents: [],
          registrationFields: eventData.registrationFields || [],
          regulation: null,
          registrationMode: eventData.registrationMode || 'team'
        });

        setLoading(false);
      } catch (err) {
        console.error('Error fetching event details:', err);
        if (err.response?.status === 401) {
          console.log('Unauthorized access, redirecting to login');
          localStorage.removeItem('token');
          navigate('/login');
        } else if (err.response?.status === 404) {
          setError('Evento non trovato');
        } else {
          setError(err.response?.data?.message || 'Errore nel caricamento dei dettagli dell\'evento');
        }
        setLoading(false);
      }
    };

    if (id) {
      fetchEventDetails();
    } else {
      setError('ID evento mancante');
      setLoading(false);
    }
  }, [id, navigate]);

  // Handle successful submission
  const handleSuccess = (data) => {
    console.log('Event updated successfully:', data);
    setSuccess(true);

    // Show the notification for a moment before redirecting
    setTimeout(() => {
      // Add event ID to the admin events page so it can be highlighted
      navigate('/admin/events', {
        state: {
          updatedEventId: id,
          successMessage: 'Evento aggiornato con successo!'
        }
      });
    }, 2000);
  };

  // Handle error on form submission
  const handleError = (errorMsg) => {
    console.error('Error updating event:', errorMsg);
    setError(errorMsg || 'Errore durante l\'aggiornamento dell\'evento');
    // Scroll to top to show the error message
    window.scrollTo(0, 0);
  };

  // Function to handle manual update of the event
  const handleManualUpdate = async (eventData) => {
    try {
      setError('');
      setLoading(true);

      const token = localStorage.getItem('token');
      if (!token) {
        setError('Sessione scaduta. Effettua nuovamente il login.');
        setLoading(false);
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      // Create FormData object for submission
      let dataToSend = new FormData();

      // Add basic text fields
      dataToSend.append('title', eventData.title);
      dataToSend.append('description', eventData.description);
      dataToSend.append('location', eventData.location);
      dataToSend.append('eventDate', eventData.eventDate);
      dataToSend.append('eventEndDate', eventData.eventEndDate || eventData.eventDate);
      dataToSend.append('startTime', eventData.startTime);
      dataToSend.append('endTime', eventData.endTime);
      dataToSend.append('registrationDeadline', eventData.registrationDeadline);
      dataToSend.append('maxParticipants', eventData.maxParticipants ? Number(eventData.maxParticipants) : 0);
      dataToSend.append('price', eventData.price ? Number(eventData.price) : 0);
      dataToSend.append('category', eventData.category);
      dataToSend.append('scope', eventData.scope || 'territorial');
      dataToSend.append('isPublic', eventData.isPublic);
      dataToSend.append('allowLateRegistration', eventData.allowLateRegistration);
      dataToSend.append('registrationMode', eventData.registrationMode);

      // Handle registration fields properly
      if (eventData.registrationFields && eventData.registrationFields.length > 0) {
        const regFieldsJson = JSON.stringify(eventData.registrationFields);
        dataToSend.append('registrationFields', regFieldsJson);
      }

      // Add image if exists
      if (eventData.image) {
        dataToSend.append('image', eventData.image);
      }

      // Add documents if they exist
      if (eventData.documents && eventData.documents.length > 0) {
        Array.from(eventData.documents).forEach(doc => {
          dataToSend.append('documents', doc);
        });
      }

      // Audio files are now uploaded by associations

      console.log('Submitting updated event data to server...');
      const response = await axiosInstance.put(`/api/events/${id}`, dataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      handleSuccess(response.data);
    } catch (err) {
      handleError(err.response?.data?.message || 'Errore durante l\'aggiornamento dell\'evento');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container fluid className='min-vh-100 d-flex justify-content-center align-items-center'
        style={{ background: 'linear-gradient(135deg, #006e39, #52b788)' }}>
        <div className='text-center'>
          <Spinner animation='border' variant='light' />
          <p className='text-white mt-3'>Caricamento dettagli evento...</p>
        </div>
      </Container>
    );
  }

  return (
    <div className="container-fluid min-vh-100" style={{ backgroundColor: '#ffffff' }}>
      <div className="row p-4">
        <div className="col-12 col-md-8 col-lg-6 mx-auto">
          <div className="glass-container p-4 rounded-4"
            style={{ backgroundColor: 'white', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
            <h2 style={uispStyles.header} className="mb-4">
              <i className="bi bi-pencil-square me-2"></i>
              Modifica Evento
            </h2>

            {success && (
              <div className="alert alert-success">
                <i className="bi bi-check-circle-fill me-2"></i>
                Evento aggiornato con successo! Reindirizzamento...
              </div>
            )}

            {error && (
              <div className="alert alert-danger alert-dismissible">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {error}
                <button type="button" className="btn-close" onClick={() => setError('')}></button>
              </div>
            )}

            {event ? (
              <EventForm
                event={event}
                isEdit={true}
                onSuccess={handleSuccess}
                onError={handleError}
              />
            ) : error ? null : (
              <div className="alert alert-warning">
                <i className="bi bi-exclamation-circle-fill me-2"></i>
                Evento non trovato o dati mancanti.
              </div>
            )}

            <div className="d-flex justify-content-between mt-4">
              <button
                type="button"
                className="btn"
                style={uispStyles.secondaryButton}
                onClick={() => {
                  try {
                    console.log('Annulla button clicked');
                    navigate('/admin/events');
                  } catch (error) {
                    console.error('Error navigating back:', error);
                  }
                }}
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditEvent;
